import { useState } from 'react';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { Button } from '../../../../components/Clickables/Buttons/Button';
import { StarIcon } from '../../../../components/Icons/StarIcon';
import { RatingVariant } from '../../../../components/Rating/constants/constants';
import { Rating } from '../../../../components/Rating/Rating';
import { Text } from '../../../../components/Text/Text';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';

interface IProps {
  averageRating: number;
  reviewCount: number;
  hasWrittenReviews: boolean;
  oneStarReviews?: number;
  twoStarReviews?: number;
  threeStarReviews?: number;
  fourStarReviews?: number;
  fiveStarReviews?: number;
  sourceId?: string;
}

const RATING_ICON_WIDTH = 13;

const getRatingRatio = (reviewCount: number, allReviewsCount: number) => {
  return reviewCount > 0 ? (reviewCount / allReviewsCount) * 100 : 0;
};

export const RatingsSummary = ({
  averageRating,
  reviewCount,
  hasWrittenReviews,
  oneStarReviews,
  twoStarReviews,
  threeStarReviews,
  fourStarReviews,
  fiveStarReviews,
  sourceId,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const ratings = [
    { rating: 5, ratio: getRatingRatio(fiveStarReviews ?? 0, reviewCount) },
    { rating: 4, ratio: getRatingRatio(fourStarReviews ?? 0, reviewCount) },
    { rating: 3, ratio: getRatingRatio(threeStarReviews ?? 0, reviewCount) },
    { rating: 2, ratio: getRatingRatio(twoStarReviews ?? 0, reviewCount) },
    { rating: 1, ratio: getRatingRatio(oneStarReviews ?? 0, reviewCount) },
  ];

  const handleButtonClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'WriteReviewButton',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div className="flex w-full flex-col items-center justify-center gap-6 sm:w-96">
      <div className="flex w-full flex-row items-center justify-center gap-4">
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="font-heading text-trench text-[52px] font-semibold">
            {averageRating.toFixed(1)}
          </div>
          <Rating
            className=""
            rating={averageRating}
            variant={RatingVariant.allStars}
            hideLabel
            iconSize={RATING_ICON_WIDTH}
          />
          <Text variant="xs" className="text-abyss m-0">{`${reviewCount} ratings`}</Text>
        </div>
        <div className="flex grow flex-col">
          {ratings.map(({ rating, ratio }) => (
            <div key={rating} className="flex flex-row items-center gap-2">
              <span className="text-trench w-4 text-center">{rating}</span>
              <span
                className={`flex items-center justify-center ${ratio > 0 ? 'text-clownfish' : 'text-twilight'}`}
              >
                <StarIcon height={11} width={11} />
              </span>
              <span className="bg-twilight relative h-[5px] w-full grow rounded-md">
                <span
                  style={{ width: `${ratio}%` }}
                  className="bg-clownfish absolute inset-0 h-[5px] rounded-md"
                ></span>
              </span>
            </div>
          ))}
        </div>
      </div>
      {hasWrittenReviews && (
        <Button onClick={handleButtonClick} variant="buttonSecondary" className="w-full">
          Write a review
        </Button>
      )}

      <AppDownloadModal
        appDownloadSource="RatingsSummary"
        appDownloadSourceId={sourceId}
        modalHeader={'Download the Fishbrain app to write a review'}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
